import { TaskManager } from '@euler/app/flows/task/functions/task-detail/TaskManager';
import { TVNavParams } from '@euler/app/Routes.tv';
import { EmptyTaskListContent } from '@euler/app/tv/components/dashboard/EmptyTaskListContent';
import { TaskPackCell } from '@euler/app/tv/components/dashboard/TaskPackCell';
import {
  TaskListMode,
  TaskPack,
  useDashboardTaskListMetrics,
} from '@euler/app/tv/functions/useDashboardTaskListMetrics';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleInspectionTask } from '@euler/model/entity';
import { safeMarkDevFlag } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  StyleProp,
  TextStyle,
  ViewStyle,
} from 'react-native';

const kFadeEmptyList = safeMarkDevFlag(false);

type Props = {
  listMode: TaskListMode;
  maxTaskPerPack?: number;
  maxPackCapacity: number;
  cellWidth: number;
  cellHeight: number;
  cellMargin: number;
  numberOfColumns: number;
  isLoading?: boolean;
  error?: Error;
  isRefreshing?: boolean;
  hasMore?: boolean;
  packs: TaskPack<VehicleInspectionTask>[];
  showStatus?: boolean;
  showFinishedTime?: boolean;
  emptyText?: string;
  emptyIconSize?: number;
  emptyTextStyle?: StyleProp<TextStyle>;
  emptyButtonStyle?: StyleProp<ViewStyle>;
  emptyButtonTextStyle?: StyleProp<TextStyle>;
  emptyErrorMessage?: string;
  onEndReached?: () => void;
  onRefresh?: () => void;
  onTaskPress?: (task: VehicleInspectionTask) => void;
};

export const DashboardTaskList = memo(
  ({
    listMode,
    maxTaskPerPack,
    maxPackCapacity,
    packs,
    cellWidth,
    cellHeight,
    cellMargin,
    numberOfColumns,
    isLoading,
    isRefreshing,
    error,
    showStatus,
    showFinishedTime,
    emptyText,
    emptyIconSize,
    emptyTextStyle,
    emptyButtonStyle,
    emptyButtonTextStyle,
    emptyErrorMessage,
    onEndReached,
    onTaskPress,
    onRefresh,
  }: Props) => {
    const { rpx } = useResponsiveContext();
    const [paddingLeft, paddingRight] = useScreenContentPadding();
    const renderItem: ListRenderItem<TaskPack<VehicleInspectionTask>> =
      usePersistFn(({ item }) => {
        return (
          <TaskPackCell
            mode={listMode}
            pack={item}
            width={cellWidth}
            height={cellHeight}
            margin={cellMargin}
            showFinishedTime={showFinishedTime}
            showStatus={showStatus}
            onTaskPress={onTaskPress}
          />
        );
      });

    if (kFadeEmptyList) {
      return (
        <EmptyTaskListContent
          loading={true}
          cellWidth={cellWidth}
          cellHeight={
            listMode === 'vertical-compact' ? cellHeight * 1.5 : cellHeight
          }
          cellMargin={cellMargin}
          numberOfRows={
            listMode === 'horizontal-complete'
              ? maxTaskPerPack ?? maxPackCapacity
              : listMode === 'vertical-compact'
              ? 8
              : 6
          }
          emptyText={emptyText}
          emptyIconSize={emptyIconSize}
          emptyTextStyle={emptyTextStyle}
          emptyButtonStyle={emptyButtonStyle}
          emptyButtonTextStyle={emptyButtonTextStyle}
          emptyErrorMessage={emptyErrorMessage}
          onRefresh={onRefresh}
        />
      );
    }

    if (isLoading || error || !packs.length) {
      return (
        <EmptyTaskListContent
          loading={isLoading}
          error={error}
          cellWidth={cellWidth}
          cellHeight={
            listMode === 'vertical-compact' ? cellHeight * 1.5 : cellHeight
          }
          cellMargin={cellMargin}
          numberOfRows={
            listMode === 'horizontal-complete'
              ? maxTaskPerPack ?? maxPackCapacity
              : listMode === 'vertical-compact'
              ? 8
              : 6
          }
          emptyText={emptyText}
          emptyIconSize={emptyIconSize}
          emptyTextStyle={emptyTextStyle}
          emptyButtonStyle={emptyButtonStyle}
          emptyButtonTextStyle={emptyButtonTextStyle}
          emptyErrorMessage={emptyErrorMessage}
          onRefresh={onRefresh}
        />
      );
    }

    return (
      <FlatList
        data={packs}
        key={listMode}
        keyExtractor={pack => pack.tasks.map(x => x.taskNo).join(',')}
        renderItem={renderItem}
        horizontal={listMode === 'horizontal-complete'}
        numColumns={
          listMode === 'horizontal-complete' ? undefined : numberOfColumns
        }
        onEndReachedThreshold={0.5}
        onEndReached={onEndReached}
        contentContainerStyle={{
          alignItems: listMode === 'horizontal-complete' ? 'center' : undefined,
          paddingLeft,
          paddingRight: listMode === 'horizontal-complete' ? 0 : paddingRight,
        }}
        refreshControl={
          Platform.OS === 'web' ||
          listMode === 'horizontal-complete' ? undefined : (
            <RefreshControl
              refreshing={isRefreshing ?? false}
              onRefresh={onRefresh}
              progressViewOffset={40}
            />
          )
        }
        css={`
          margin-top: ${listMode === 'horizontal-complete' ? 0 : rpx(44)}px;
        `}
      />
    );
  },
);

export const WiredDashboardTaskList = memo(
  ({
    tasks,
    preferredMode,
    maxTaskPerPack,
    showStatus,
    showFinishedTime,
    isRefreshing,
    isLoading,
    hasMore,
    error,
    emptyText,
    emptyIconSize,
    emptyTextStyle,
    emptyButtonStyle,
    emptyButtonTextStyle,
    emptyErrorMessage,
    onFetchMore,
    onRefresh,
  }: {
    debugLabel: string;
    tasks: VehicleInspectionTask[];
    preferredMode: TaskListMode;
    maxTaskPerPack?: number;
    showStatus?: boolean;
    showFinishedTime?: boolean;
    onRefresh?: () => void;
    isRefreshing?: boolean;
    isLoading?: boolean;
    hasMore?: boolean;
    emptyText?: string;
    emptyTextStyle?: StyleProp<TextStyle>;
    emptyIconSize?: number;
    emptyButtonStyle?: StyleProp<ViewStyle>;
    emptyButtonTextStyle?: StyleProp<TextStyle>;
    emptyErrorMessage?: string;
    error?: Error;
    onFetchMore?: () => void;
  }) => {
    const navigation = useNavigation<StackNavigationProp<TVNavParams>>();

    const onEndReached = useCallback(() => {
      onFetchMore?.();
    }, [onFetchMore]);

    const onTaskPress = useCallback(
      (task: VehicleInspectionTask) => {
        TaskManager.tryRecover(task.taskNo);
        navigation.navigate('Task', {
          taskNo: task.taskNo,
        });
      },
      [navigation],
    );

    const {
      cellWidth,
      cellHeight,
      cellMargin,
      numberOfColumns,
      packs,
      mode,
      maxPackCapacity,
    } = useDashboardTaskListMetrics({ preferredMode, tasks, maxTaskPerPack });

    return (
      <DashboardTaskList
        listMode={mode}
        maxTaskPerPack={maxTaskPerPack}
        maxPackCapacity={maxPackCapacity}
        packs={packs}
        cellWidth={cellWidth}
        cellHeight={cellHeight}
        cellMargin={cellMargin}
        numberOfColumns={numberOfColumns}
        showStatus={showStatus}
        showFinishedTime={showFinishedTime}
        onEndReached={onEndReached}
        isLoading={isLoading}
        hasMore={hasMore}
        error={error}
        isRefreshing={isRefreshing}
        emptyText={emptyText}
        emptyIconSize={emptyIconSize}
        emptyTextStyle={emptyTextStyle}
        emptyButtonStyle={emptyButtonStyle}
        emptyButtonTextStyle={emptyButtonTextStyle}
        emptyErrorMessage={emptyErrorMessage}
        onRefresh={onRefresh}
        onTaskPress={onTaskPress}
      />
    );
  },
);
